import { Component, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, finalize, shareReplay, startWith, tap } from 'rxjs/operators';
import { Country, Region, OccSiteAdapter, GlobalMessageService, GlobalMessageType } from '@spartacus/core';
import { UserAccountService } from '../../../../feature-libs/kn-br-user/kn-br-account/core/facade/user-account.service';
import { AtgUserManagementService } from '../atg-user-management.service';

interface SalesArea {
  accountCode: string;
  accountName: string;
  b2bUnitPk: string;
  b2bUnitUid: string;
  customerNumber: string;
  salesArea: string;
  selected: boolean;
}

interface AccountResult {
  id: string;
  accountName: string;
  division: string[];
  salesAreaList: SalesArea[];
  salesAreaItems: { value: string; label: string }[];
  city: string;
  state: string;
  country: string;
  zipCode: string;
  accountNumber: string;
  accountGroup: string;
  selected: boolean;
}

interface SelectedAccount {
  id: string;
  accountGroup: string;
  accountNumber: string;
  division: string[];
  salesAreaList: SalesArea[];
  accountName: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  markedForDelete: boolean;
}

@Component({
  selector: 'atg-add-edit-users-list',
  templateUrl: './atg-add-edit-users-list.component.html',
  styleUrl: './atg-add-edit-users-list.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AtgAddEditUsersListComponent implements OnInit, OnChanges {
  private readonly divisionCodeMap: Record<string, string> = {
    HTEBSUS: '1201_10_20',
    HTEBSUSRETAIL: '1201_20_20',
    HTEBSUSDIRECT: '1201_30_20',
    HTEBSCA: '2700_10_20',
    HTEBSCADIRECT: '2700_30_20',
    HTEBSCARETAIL: '2700_20_20',
    PTEBSUS: '1215_10_20',
    PTEBSUSDIRECT: '1215_30_20',
  };

  showAccountModal = false;
  showSearchResults = false;
  showAccountDetailsModal = false;
  searchForm!: FormGroup;

  countries$: Observable<Country[]> | null = null;
  states$: Observable<Region[]> | null = null;
  isCountryLoading = false;
  isStateLoading = false;

  @Output() accountsPayload = new EventEmitter<any[]>();
  @Input() userRole: string | null = null;
  @Input() initialParentSoldToList: any[] | null = null;

  userId = '';
  isSearching = false;

  searchResults: AccountResult[] = [];
  selectedAccounts: SelectedAccount[] = [];
  selectedAccountDetails: SelectedAccount | null = null;

  divisions = [
    { value: 'HTEBSUS', label: 'Hand Tools US' },
    { value: 'HTEBSUSRETAIL', label: 'Hand Tools Retail US' },
    { value: 'HTEBSUSDIRECT', label: 'Hand Tools Direct US' },
    { value: 'HTEBSCA', label: 'Hand Tools Canada' },
    { value: 'HTEBSCADIRECT', label: 'Hand Tools Direct Canada' },
    { value: 'HTEBSCARETAIL', label: 'Hand Tools Retail Canada' },
    { value: 'PTEBSUS', label: 'Power Tools US' },
    { value: 'PTEBSUSDIRECT', label: 'Power Tools Direct US' },
  ];

  private readonly backendAccountTypes = [
    { value: 'AGENT', label: 'Agent' },
    { value: 'DISTRIBUTOR', label: 'Distributor' },
    { value: 'ACCOUNT_GROUP', label: 'Account Group' },
    { value: 'SALES_EMPLOYEE', label: 'Sales Employee' },
  ];

  private readonly roleToAccountTypeMap: Record<string, string[]> = {
    ATG_ADMIN: ['DISTRIBUTOR'],
    HAND_TOOLS_ADMIN: ['DISTRIBUTOR'],
    POWER_TOOLS_ADMIN: ['DISTRIBUTOR'],
    AGENT_USER: ['AGENT'],
    AGENT_ADMIN: ['AGENT'],
    AGENT_USER_NO_PRICING: ['AGENT'],
    HAND_TOOLS_SALES: ['SALES_EMPLOYEE', 'DISTRIBUTOR'],
    POWER_TOOLS_SALES: ['SALES_EMPLOYEE', 'DISTRIBUTOR'],
    HAND_TOOLS_USER: ['DISTRIBUTOR'],
    POWER_TOOLS_USER: ['DISTRIBUTOR'],
    DISTRIBUTOR_ADMIN: ['ACCOUNT_GROUP', 'DISTRIBUTOR'],
    DISTRIBUTOR_GROUP_USER: ['ACCOUNT_GROUP'],
    BRANCH_ADMIN: ['ACCOUNT_GROUP', 'DISTRIBUTOR'],
    DISTRIBUTOR_USER: ['DISTRIBUTOR'],
    DISTRIBUTOR_USER_NO_PRICING: ['DISTRIBUTOR'],
  };

  accountTypes = [...this.backendAccountTypes];

  constructor(
    private formBuilder: FormBuilder,
    private occSiteAdapter: OccSiteAdapter,
    private cdr: ChangeDetectorRef,
    private userManagementService: AtgUserManagementService,
    private currentUserService: UserAccountService,
    private globalMessageService: GlobalMessageService
  ) {}

  ngOnInit(): void {
    this.initializeSearchForm();
    this.applyUserRoleAccountTypeRules();
    this.loadCountries();
    this.subscribeToCountryChanges();

    if (this.initialParentSoldToList && this.initialParentSoldToList.length > 0) {
      this.loadInitialParentSoldToList();
    } else {
      this.selectedAccounts = [];
    }

    this.currentUserService.get().subscribe((user: any) => {
      this.userId = user?.uid ?? '';
    });
  }

  private subscribeToCountryChanges(): void {
    this.searchForm.get('country')?.valueChanges
      .pipe(
        startWith(this.searchForm.get('country')?.value),
        debounceTime(300)
      )
      .subscribe((countryIsoCode: string) => {
        this.onCountrySelected(countryIsoCode);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userRole'] && this.searchForm) {
      this.applyUserRoleAccountTypeRules();
    }

    if (changes['initialParentSoldToList'] && this.initialParentSoldToList) {
      this.loadInitialParentSoldToList();
    }
  }

  private initializeSearchForm(): void {
    this.searchForm = this.formBuilder.group({
      division: ['', Validators.required],
      accountName: [''],
      accountNumber: [''],
      accountType: ['', Validators.required],
      city: [''],
      state: [''],
      country: [''],
      zipCode: [''],
      b2bUserType: [''],
    });
  }

  private loadInitialParentSoldToList(): void {
    const dataToLoad = this.initialParentSoldToList;
    if (!dataToLoad || dataToLoad.length === 0) {
      return;
    }

    this.selectedAccounts = dataToLoad.map((parent: any) => {
      const salesAreaList = parent.salesAreaList || [];

      const selectedDivisions = this.getSelectedAccountCodes(salesAreaList);
      const allDivisions = this.getUniqueAccountCodes(salesAreaList);

      return {
        id: parent.accountNumber || String(Math.random()),
        accountGroup: parent.accountGroup || this.getAccountGroupFromSalesAreaList(salesAreaList),
        accountNumber: parent.accountNumber || '',
        division: selectedDivisions.length > 0 ? selectedDivisions : allDivisions,
        salesAreaList: salesAreaList,
        accountName: parent.name || '',
        city: parent.city || '',
        state: parent.state || '',
        country: parent.country || '',
        zipCode: parent.postalCode || '',
        markedForDelete: false,
      };
    });

    this.accountsPayload.emit(this.getPayload());
    this.cdr.markForCheck();
  }

  private normalizeRole(value?: string | null): string {
    return (value || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  }

  private getAccountGroupFromSalesAreaList(salesAreaList: any[]): string {
    const accountGroups = Array.from(new Set((salesAreaList || [])
      .map((sa: any) => sa.accountGroup)
      .filter((group: any) => !!group)));

    return accountGroups.length === 1 ? accountGroups[0] : accountGroups.join(', ');
  }

  private getUniqueAccountCodes(salesAreaList: any[]): string[] {
    return Array.from(new Set((salesAreaList || [])
      .map((sa: any) => sa.accountCode?.toString().trim())
      .filter((code: any) => !!code)));
  }

  private getSelectedAccountCodes(salesAreaList: any[]): string[] {
    return Array.from(new Set((salesAreaList || [])
      .filter((sa: any) => sa.selected)
      .map((sa: any) => sa.accountCode?.toString().trim())
      .filter((code: any) => !!code)));
  }

  private getSalesAreaItems(salesAreaList: any[]): { value: string; label: string }[] {
    const map = new Map<string, { value: string; label: string }>();
    (salesAreaList || []).forEach((sa: any) => {
      const accountCode = sa?.accountCode?.toString().trim() || '';
      const accountName = sa?.accountName?.toString().trim() || accountCode;
      if (!accountCode && !accountName) {
        return;
      }

      const key = accountCode || accountName;
      if (!map.has(key)) {
        map.set(key, { value: accountCode || accountName, label: accountName });
      }
    });
    return Array.from(map.values());
  }

  private applyUserRoleAccountTypeRules(): void {
    const normalizedRole = this.normalizeRole(this.userRole);
    const allowedTypes = this.roleToAccountTypeMap[normalizedRole] || this.backendAccountTypes.map((t) => t.value);

    this.accountTypes = this.backendAccountTypes.filter((option) => allowedTypes.includes(option.value));

    const selectedAccountType = this.searchForm.get('accountType')?.value;
    if (!selectedAccountType || !allowedTypes.includes(selectedAccountType)) {
      this.searchForm.patchValue(
        { accountType: this.accountTypes.length === 1 ? this.accountTypes[0].value : '' },
        { emitEvent: false }
      );
    }

    this.searchForm.patchValue({ b2bUserType: normalizedRole || '' }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  private loadCountries(): void {
    this.isCountryLoading = true;
    this.countries$ = this.occSiteAdapter.loadCountries().pipe(
      tap(() => (this.isCountryLoading = false)),
      shareReplay(1)
    );
  }

  openAccountModal(): void {
    // Check if userRole is selected before opening the modal
    if (!this.userRole || this.userRole.trim() === '') {
      this.globalMessageService.add(
        { raw: 'Please select a User Role before adding accounts.' },
        GlobalMessageType.MSG_TYPE_ERROR
      );
      return;
    }

    // Ensure b2bUserType is set to the current userRole
    this.applyUserRoleAccountTypeRules();

    this.showAccountModal = true;
    this.showSearchResults = false;
    this.searchResults = [];

    this.cdr.markForCheck();
  }

  closeAccountModal(): void {
    this.showAccountModal = false;
    this.showSearchResults = false;
    this.resetSearchForm();
  }

  private mapDivisionCode(code: string): string {
    return this.divisionCodeMap[code] || code;
  }

  onCountrySelected(countryIsoCode: string): void {
    this.searchForm.get('state')?.reset('', { emitEvent: false });

    if (countryIsoCode) {
      this.isStateLoading = true;
      this.states$ = this.occSiteAdapter.loadRegions(countryIsoCode).pipe(
        tap(() => {
          this.isStateLoading = false;
          this.cdr.markForCheck();
        }),
        shareReplay(1)
      );
    } else {
      this.states$ = null;
    }
    // This runs from a debounced (async) callback on an OnPush component;
    // without this, the view never notices states$ changed, so the async pipe
    // never subscribes and loadRegions() is never actually triggered.
    this.cdr.markForCheck();
  }

  onSearch(): void {
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const formValues = this.searchForm.value;
    const searchParams = {
      city: formValues.city || undefined,
      state: formValues.state || undefined,
      postalCode: formValues.zipCode || undefined,
      country: formValues.country || undefined,
      accountNumber: formValues.accountNumber || undefined,
      accountType: formValues.accountType,
      salesAreaName: this.mapDivisionCode(formValues.division),
      accountName: formValues.accountName || undefined,
      b2bUserType: formValues.b2bUserType || undefined,
    };

    this.isSearching = true;
    this.cdr.markForCheck();
    this.userManagementService.searchAccounts(searchParams)
      .pipe(finalize(() => {
        this.isSearching = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: (response) => {
          const parentSoldToList = response?.parentSoldToList ?? [];
          this.searchResults = parentSoldToList.map((parent: any, index: number) => {
            const resultId = parent.accountNumber || String(index + 1);
            const existingAccount = this.selectedAccounts.find(acc => acc.id === resultId);
            const isAlreadySelected = !!existingAccount;

            return {
              id: resultId,
              accountName: parent.name || '',
              division: isAlreadySelected && existingAccount
                ? [...existingAccount.division]
                : this.getSelectedAccountCodes(parent.salesAreaList || []),
              salesAreaList: parent.salesAreaList || [],
              salesAreaItems: this.getSalesAreaItems(parent.salesAreaList || []),
              city: parent.city || '',
              state: parent.state || '',
              country: parent.country || '',
              zipCode: parent.postalCode || '',
              accountNumber: parent.accountNumber || '',
              accountGroup: parent.accountGroup || this.getAccountGroupFromSalesAreaList(parent.salesAreaList || []),
              selected: isAlreadySelected,
            };
          });
          this.showSearchResults = true;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error searching accounts:', err);
          this.searchResults = [];
          this.showSearchResults = true;
          this.cdr.markForCheck();
        },
      });
  }

  resetSearchForm(): void {
    // Preserve b2bUserType when resetting the form
    const currentB2bUserType = this.searchForm.get('b2bUserType')?.value;
    this.searchForm.reset();
    this.searchForm.patchValue({ b2bUserType: currentB2bUserType || '' }, { emitEvent: false });
    this.states$ = null;
    this.showSearchResults = false;
  }

  newSearch(): void {
    this.resetSearchForm();
    this.showSearchResults = false;
  }

  toggleSelectAll(selectAll: boolean): void {
    this.searchResults.forEach(result => {
      result.selected = selectAll;
    });
  }

  isAllSelected(): boolean {
    return this.searchResults.length > 0 && this.searchResults.every(r => r.selected);
  }

  trackByResultId(index: number, result: AccountResult): string {
    return result.id;
  }

  getDivisionLabel(divisionCode: string): string {
    const division = this.divisions.find(d => d.value === divisionCode);
    return division ? division.label : divisionCode;
  }

  getDivisionLabels(divisionCodes: string[]): string {
    return divisionCodes.map(code => this.getDivisionLabel(code)).join(', ');
  }

  toggleAccountSelection(id: string): void {
    const result = this.searchResults.find(r => r.id === id);
    if (result) {
      result.selected = !result.selected;
    }
  }

  private mergeSalesAreaLists(existing: SalesArea[], incoming: SalesArea[]): SalesArea[] {
    const merged = new Map<string, SalesArea>();
    (existing || []).forEach(sa => merged.set(sa.accountCode, sa));
    (incoming || []).forEach(sa => merged.set(sa.accountCode, sa));
    return Array.from(merged.values());
  }

  addSelectedAccounts(): void {
    const selectedResults = this.searchResults.filter(r => r.selected);
    selectedResults.forEach(result => {
      const existingAccountIndex = this.selectedAccounts.findIndex(acc => acc.id === result.id);
      if (existingAccountIndex >= 0) {
        const existingAccount = this.selectedAccounts[existingAccountIndex];
        existingAccount.division = [...result.division];
        existingAccount.accountGroup = result.accountGroup || '';
        existingAccount.salesAreaList = this.mergeSalesAreaLists(existingAccount.salesAreaList, result.salesAreaList);
      } else {
        this.selectedAccounts.push({
          id: result.id,
          accountGroup: result.accountGroup || '',
          accountNumber: result.accountNumber,
          division: [...result.division],
          salesAreaList: result.salesAreaList,
          accountName: result.accountName,
          city: result.city,
          state: result.state,
          country: result.country,
          zipCode: result.zipCode,
          markedForDelete: false,
        });
      }
    });
    this.accountsPayload.emit(this.getPayload());
    this.cdr.markForCheck();
    this.closeAccountModal();
  }

  getPayload(): any[] {
    return this.selectedAccounts
      .filter(acc => !acc.markedForDelete)
      .map(acc => {
        const mappedDivisionSet = new Set(acc.division.map(code => this.mapDivisionCode(code)));

        return {
          accountNumber: acc.accountNumber,
          accountGroup: acc.accountGroup,
          city: acc.city,
          country: acc.country,
          name: acc.accountName,
          postalCode: acc.zipCode,
          state: acc.state,
          salesAreaList: acc.salesAreaList
            .map(sa => {
              const mappedAccountCode = this.mapDivisionCode(sa.accountCode);
              const mappedSalesArea = this.mapDivisionCode(sa.salesArea || sa.accountCode);
              return {
                ...sa,
                accountCode: mappedAccountCode,
                salesArea: mappedSalesArea,
                selected: true,
              };
            })
            .filter(sa => mappedDivisionSet.has(sa.accountCode)),
        };
      });
  }

  selectAllDivisions(resultId: string): void {
    const result = this.searchResults.find(r => r.id === resultId);
    if (result) {
      result.division = result.salesAreaItems.map(item => item.value);
      this.cdr.markForCheck();
    }
  }

  unselectAllDivisions(resultId: string): void {
    const result = this.searchResults.find(r => r.id === resultId);
    if (result) {
      result.division = [];
      this.cdr.markForCheck();
    }
  }

  getMarkedForDeleteCount(): number {
    return this.selectedAccounts.filter(acc => acc.markedForDelete).length;
  }

  deleteSelectedAccounts(): void {
    this.selectedAccounts = this.selectedAccounts.filter(acc => !acc.markedForDelete);
    this.accountsPayload.emit(this.getPayload());
    this.cdr.markForCheck();
  }

  openAccountDetailsModal(account: SelectedAccount): void {
    this.selectedAccountDetails = account;
    this.showAccountDetailsModal = true;
    this.cdr.markForCheck();
  }

  closeAccountDetailsModal(): void {
    this.showAccountDetailsModal = false;
    this.selectedAccountDetails = null;
    this.cdr.markForCheck();
  }

  getSalesAreaLabel(accountCode: string): string {
    return this.getDivisionLabel(accountCode);
  }

  loadParentSoldToList(): void {
    this.loadInitialParentSoldToList();
  }
}
